import { wallOpeningOpening3, wallOpeningOffset3 } from "building-common/openings/openingQuery";
import type { OpeningSideName } from "building-types/openings/openingTypes";
import type { OpeningWrappingMode } from "building-types/openings/openingWrappingModes";
import { OpeningWrappingModeEnum } from "building-types/openings/openingWrappingModes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { SolidWallSide } from "building-types/walls/wallTypes";
import type { WallMassBase } from "building-types/walls/wallTypes";
import { ensureDefined } from "common/utils/assertOfType";
import { DIST_EPSILON } from "geom/core";
import { line2Normal } from "geom/line2";
import { polyLine2Simplify, polyLine2ConvexHull, polyLine2Close } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3ToPolyLine2IgnoreZ } from "geom/polyLine3";
import { vec2Add, vec2Dist, vec2IsInEdgeBounds, vec2Lerp, vec2Normalize, vec2Scale } from "geom/vec2";

export function wallSideFinishMassWrapCrossSection({
  opening,
  wrappingMode,
  mass,
  wrapDepth,
  isLeftSide,
}: {
  opening: WallOpening;
  wrappingMode: OpeningWrappingMode;
  mass: Pick<WallMassBase, "thickness" | "leftEdge" | "rightEdge"> & {
    leftSide: SolidWallSide;
    rightSide: SolidWallSide;
  };
  wrapDepth: number;
  isLeftSide: boolean;
}): PolyLine2[] {
  const result: PolyLine2[] = [];

  if (!opening.hasWrapping || wrapDepth < DIST_EPSILON) {
    return result;
  }

  result.push(...wrapMass2(opening, wrappingMode, mass, wrapDepth, isLeftSide));

  return result;
}

/**
 * determines the start and end points of the opening relative to the orientation of the mass
 */
export function openingStartEnd(
  orientTo: PolyLine2,
  opening3: PolyLine3,
): { start: [number, number]; end: [number, number] } {
  const opening2 = polyLine2Simplify(polyLine2ConvexHull(polyLine3ToPolyLine2IgnoreZ(opening3)));

  const v0x = opening2[0];
  const v0y = opening2[1];

  const v1x = opening2[2];
  const v1y = opening2[3];

  // the convex hull algorithm may have reversed the order of the points
  if (vec2Dist(orientTo[0], orientTo[1], v0x, v0y) > vec2Dist(orientTo[0], orientTo[1], v1x, v1y)) {
    return {
      start: [v1x, v1y],
      end: [v0x, v0y],
    };
  }

  return {
    start: [v0x, v0y],
    end: [v1x, v1y],
  };
}

function wrapMass2(
  opening: WallOpening,
  wrappingMode: OpeningWrappingMode,
  mass: Pick<WallMassBase, "thickness" | "leftEdge" | "rightEdge">,
  wrapDepth: number,
  isLeftSide: boolean,
): PolyLine2[] {
  const result: PolyLine2[] = [];

  if (wrappingMode === OpeningWrappingModeEnum.NONE) {
    return result;
  }

  const { thickness, leftEdge, rightEdge } = mass;

  let side: OpeningSideName = "FRONT";
  let otherSide: OpeningSideName = "BACK";

  if (isLeftSide) {
    side = "BACK";
    otherSide = "FRONT";
    switch (wrappingMode) {
      case OpeningWrappingModeEnum.EXTERIOR_CORE_FACE:
        wrappingMode = OpeningWrappingModeEnum.INTERIOR_CORE_FACE;
        break;
      case OpeningWrappingModeEnum.INTERIOR_CORE_FACE:
        wrappingMode = OpeningWrappingModeEnum.EXTERIOR_CORE_FACE;
        break;
      case OpeningWrappingModeEnum.CENTER_OF_CORE:
      default:
        break;
    }
  }

  const otherOpening3 = wallOpeningOpening3(opening, otherSide);
  const { start: bStart, end: bEnd } = openingStartEnd(leftEdge, ensureDefined(otherOpening3));

  const startInBounds = vec2IsInEdgeBounds(...bStart, leftEdge[0], leftEdge[1], leftEdge[2], leftEdge[3]);
  const endInBounds = vec2IsInEdgeBounds(...bEnd, leftEdge[0], leftEdge[1], leftEdge[2], leftEdge[3]);

  // early exit if neither side of the opening overlaps the finish mass
  if (!startInBounds && !endInBounds) {
    return result;
  }

  const opening3 = wallOpeningOpening3(opening, side);
  const offset3 = wallOpeningOffset3(opening, side, thickness);

  if (!opening3 || !offset3) {
    return result;
  }

  const { start: fStart, end: fEnd } = openingStartEnd(rightEdge, opening3);
  const { start: fStartOff, end: fEndOff } = openingStartEnd(rightEdge, offset3);
  const { start: bStartOff, end: bEndOff } = openingStartEnd(
    leftEdge,
    ensureDefined(wallOpeningOffset3(opening, otherSide, thickness)),
  );

  const [lnx, lny] = line2Normal(leftEdge[0], leftEdge[1], leftEdge[2], leftEdge[3]);
  const [tx, ty] = vec2Scale(lnx, lny, -thickness);

  switch (wrappingMode) {
    case OpeningWrappingModeEnum.EXTERIOR_CORE_FACE:
      if (startInBounds) {
        result.push(
          wrap(
            ...(vec2Add(...bStartOff, tx, ty) as [number, number]),
            ...(vec2Add(...bStart, tx, ty) as [number, number]),
            ...bStart,
            ...bStartOff,
            wrapDepth,
          ),
        );
      }
      if (endInBounds) {
        result.push(
          wrap(
            ...(vec2Add(...bEndOff, tx, ty) as [number, number]),
            ...(vec2Add(...bEnd, tx, ty) as [number, number]),
            ...bEnd,
            ...bEndOff,
            wrapDepth,
          ),
        );
      }
      break;
    case OpeningWrappingModeEnum.INTERIOR_CORE_FACE:
      if (startInBounds) {
        result.push(
          wrap(
            ...(vec2Add(...bStartOff, tx, ty) as [number, number]),
            ...(vec2Add(...bStart, tx, ty) as [number, number]),
            ...fStart,
            ...fStartOff,
            wrapDepth,
          ),
        );
      }
      if (endInBounds) {
        result.push(
          wrap(
            ...(vec2Add(...bEndOff, tx, ty) as [number, number]),
            ...(vec2Add(...bEnd, tx, ty) as [number, number]),
            ...fEnd,
            ...fEndOff,
            wrapDepth,
          ),
        );
      }
      break;
    case OpeningWrappingModeEnum.CENTER_OF_CORE:
      if (startInBounds) {
        result.push(wrapAtParam(bStartOff, bStart, fStart, fStartOff, tx, ty, 0.5, wrapDepth));
      }
      if (endInBounds) {
        result.push(wrapAtParam(bEndOff, bEnd, fEnd, fEndOff, tx, ty, 0.5, wrapDepth));
      }
      break;
    default:
      break;
  }

  return result;
}

export function wrapAtParam(
  backS: [number, number],
  backE: [number, number],
  frontE: [number, number],
  frontS: [number, number],
  tx: number,
  ty: number,
  param: number,
  wrapDepth: number,
): PolyLine2 {
  return wrap(
    ...(vec2Add(...backS, tx, ty) as [number, number]),
    ...(vec2Add(...backE, tx, ty) as [number, number]),
    ...(vec2Lerp(...frontE, ...backE, param) as [number, number]),
    ...(vec2Lerp(...frontS, ...backS, param) as [number, number]),
    wrapDepth,
  );
}

/**
 * generates the PolyLine2 footprint of wrapping geometry given a specific thickness
 *
 * ```
 *                      exterior (back)
 *
 *             a----------b - --/ - - - -
 *             |          :     | wrapThickness
 *             |    f-----e - --/ - - - -
 *             |    |     :
 *  (opening)  :    :           (wall)
 *
 *             :    :
 *             |    |     :
 *             d----g - - c - - - - - - -
 *
 *             |    |   interior (front)
 *             /----/
 *               ^wrapThickness
 * ```
 */
export function wrap(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number,
  wrapDepth: number,
): PolyLine2 {
  // if the wrap thickness would cause f to be outside of the allowable
  // region of the wall, return the full extents of abcd
  if (vec2Dist(ax, ay, bx, by) - DIST_EPSILON < wrapDepth || vec2Dist(ax, ay, dx, dy) - DIST_EPSILON < wrapDepth) {
    return polyLine2Close(polyLine2New([ax, ay, bx, by, cx, cy, dx, dy]));
  }

  // generate point g along wall side
  const [adnx, adny] = vec2Normalize(dx - ax, dy - ay);
  const [adx, ady] = vec2Scale(adnx, adny, wrapDepth);
  const [ex, ey] = vec2Add(bx, by, adx, ady);

  // generate point e along interior side
  const [dcnx, dcny] = vec2Normalize(cx - dx, cy - dy);
  const [dcx, dcy] = vec2Scale(dcnx, dcny, wrapDepth);
  const [gx, gy] = vec2Add(dx, dy, dcx, dcy);

  // generate corner point f
  const fx = ax + adx + dcx;
  const fy = ay + ady + dcy;

  return polyLine2Close(polyLine2New([ax, ay, bx, by, ex, ey, fx, fy, gx, gy, dx, dy]));
}
